<template>
  <div class="studioSelection">
    <v-dialog v-model="dialogVisible" fullscreen hide-overlay persistent transition="dialog-bottom-transition">
      <v-card flat class="picker-fullscreen-card">
        <!-- Floating Mobile Header: Mock Search and Canton Menu -->
        <div v-if="$vuetify.breakpoint.smAndDown && mobileView === 'map'" class="mobile-map-header glass-card">
          <v-btn icon @click="mobileView = 'list'"><v-icon>mdi-menu</v-icon></v-btn>
          <div class="mobile-search-mock" @click="mobileView = 'list'">
            <v-icon left>mdi-magnify</v-icon>
            <span>{{ searchQuery || "Studio suchen..." }}</span>
          </div>
          <v-menu offset-y transition="slide-y-transition">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on">
                <v-icon :color="selectedCanton ? 'primary' : ''">mdi-filter-variant</v-icon>
              </v-btn>
            </template>
            <v-list dense class="canton-list" max-height="300">
              <v-list-item @click="selectCanton(null)">
                <v-list-item-title class="font-weight-bold">Alle Kantone</v-list-item-title>
              </v-list-item>
              <v-list-item v-for="canton in uniqueCantons" :key="canton" @click="selectCanton(canton)">
                <v-list-item-title>{{ canton }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-btn icon @click="openFavoritesMobile">
            <v-icon :color="'grey'">
              {{ "mdi-star-outline" }}
            </v-icon>
          </v-btn>
        </div>

        <!-- Left/Main Floating Sidebar (Desktop: floating list, Mobile: full list when active) -->
        <div class="sidebar-container glass-card" :class="{ 'mobile-list-visible': mobileView === 'list' && $vuetify.breakpoint.smAndDown }">
          <!-- Sidebar Header -->
          <div class="sidebar-header">
            <div class="d-flex align-center justify-space-between mb-4">
              <div class="d-flex align-center">
                <v-btn v-if="$vuetify.breakpoint.smAndDown && mobileView === 'list'" icon class="mr-2 back-to-map-btn" @click="mobileView = 'map'">
                  <v-icon>mdi-map</v-icon>
                </v-btn>
                <h2 class="title font-weight-black text-h5 primary-text tracking-wide mb-0">STANDORTE</h2>
              </div>
              <div class="d-flex align-center">
                <!-- Canton Selector Menu -->
                <v-menu offset-y transition="slide-y-transition">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" text rounded class="canton-dropdown-btn text-capitalize">
                      {{ selectedCanton || "Alle Kantone" }}
                      <v-icon right>mdi-chevron-down</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense class="canton-list" max-height="300">
                    <v-list-item @click="selectCanton(null)">
                      <v-list-item-title class="font-weight-bold">Alle Kantone</v-list-item-title>
                    </v-list-item>
                    <v-list-item v-for="canton in uniqueCantons" :key="canton" @click="selectCanton(canton)">
                      <v-list-item-title>{{ canton }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </div>

            <!-- Search Field -->
            <v-text-field
              v-model="searchQuery"
              solo
              flat
              hide-details
              label="Studio, Ort oder Adresse suchen..."
              prepend-inner-icon="mdi-magnify"
              clearable
              class="search-bar-field mb-4"></v-text-field>

            <!-- Tabs: All / Favorites -->
            <v-tabs v-model="activeTab" class="sidebar-tabs" grow slider-color="primary">
              <v-tab href="#all" class="font-weight-bold">Alle</v-tab>
              <v-tab href="#favorites" class="font-weight-bold">
                Favoriten
                <v-avatar v-if="favoriteIds.length > 0" color="amber" size="18" class="ml-2 white--text font-weight-black text-caption">
                  {{ favoriteIds.length }}
                </v-avatar>
              </v-tab>
            </v-tabs>
          </div>

          <!-- Studio Scrollable List -->
          <div class="sidebar-scrollable-content" ref="sidebarContent">
            <v-row v-if="loading" justify="center" align="center" class="ma-0 py-12">
              <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
            </v-row>
            <template v-else-if="filteredDirectory.length > 0">
              <div v-for="group in filteredDirectory" :key="group.canton" class="canton-group">
                <div class="canton-subheader font-weight-black px-4 py-2">{{ group.canton.toUpperCase() }}</div>
                <div class="studios-list pa-2 pt-0">
                  <v-card
                    v-for="studio in group.studios"
                    :key="studio._id"
                    flat
                    class="studio-item-card mb-3"
                    :class="{ active: activeStudioId === studio._id }"
                    @click="selectStudio(studio)">
                    <div class="studio-card-body pa-4">
                      <div class="d-flex justify-space-between align-start">
                        <div class="flex-grow-1 pr-2">
                          <h3 class="studio-name font-weight-bold text-subtitle-1 mb-1">{{ studio.name }}</h3>
                          <p class="studio-addr text-caption text--secondary mb-0 text-truncate">{{ studio.adresse }}</p>
                        </div>
                        <v-btn icon @click.stop="toggleFavorite(studio._id)" class="favorite-btn mt-n1 mr-n1">
                          <v-icon :color="favoriteIds.includes(studio._id) ? 'amber' : 'grey lighten-1'">
                            {{ favoriteIds.includes(studio._id) ? "mdi-star" : "mdi-star-outline" }}
                          </v-icon>
                        </v-btn>
                      </div>

                      <div class="d-flex align-center justify-space-between mt-3 pt-3 border-top">
                        <span v-if="studio.alwaysCheckin" class="status-badge green-badge">
                          <span class="status-dot"></span>
                          24H geöffnet
                        </span>
                        <v-spacer v-else></v-spacer>

                        <v-btn depressed color="primary" small class="select-btn font-weight-bold rounded-lg" @click.stop="pickStudio(studio)">
                          Auswählen
                          <v-icon right small>mdi-arrow-right</v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </v-card>
                </div>
              </div>
            </template>
            <div v-else class="empty-state text-center py-16 px-6">
              <v-icon size="64" class="mb-4 text--disabled">
                {{ activeTab === "favorites" ? "mdi-star-off-outline" : "mdi-magnify-close" }}
              </v-icon>
              <h3 class="text-h6 font-weight-bold mb-2">
                {{ activeTab === "favorites" ? "Keine Favoriten" : "Keine Studios gefunden" }}
              </h3>
              <p class="text-body-2 text--secondary">
                {{
                  activeTab === "favorites"
                    ? "Markiere deine Lieblingsstudios mit dem Stern, um sie hier aufzulisten."
                    : "Versuche es mit einem anderen Suchbegriff oder passe die Filter an."
                }}
              </p>
            </div>
          </div>

          <!-- Detail Panel (Desktop sliding drawer within the sidebar wrapper) -->
          <transition name="slide-details">
            <div v-if="activeStudio && showDetailsPanel" class="details-panel">
              <!-- Details Content -->
              <div class="details-scroll-content flex-grow-1">
                <!-- Hero Video/Gallery -->
                <div v-if="heroPictures && heroPictures.length > 0" class="details-hero">
                  <!-- Floating transparent header controls over video -->
                  <div class="hero-overlay-controls">
                    <v-btn icon dark @click="showDetailsPanel = false" class="floating-hero-btn back">
                      <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <v-btn icon dark @click="toggleFavorite(activeStudio._id)" class="floating-hero-btn favorite">
                      <v-icon :color="favoriteIds.includes(activeStudio._id) ? 'amber' : 'white'">
                        {{ favoriteIds.includes(activeStudio._id) ? "mdi-star" : "mdi-star-outline" }}
                      </v-icon>
                    </v-btn>
                  </div>

                  <div v-if="heroVideoSrc && false" class="video-wrapper">
                    <div v-if="videoLoading" class="video-loader-overlay">
                      <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    </div>
                    <video :src="heroVideoSrc" autoplay muted loop playsinline class="hero-media-element" @loadedmetadata="onVideoLoaded"></video>
                  </div>
                  <div v-else class="gallery-wrapper">
                    <v-carousel height="100%" hide-delimiters hide-delimiter-background cycle interval="5000" class="hero-carousel">
                      <v-carousel-item v-for="(pic, i) in heroPictures" :key="i" :src="getDynamicPictureSrc(pic)" cover></v-carousel-item>
                    </v-carousel>
                  </div>
                </div>

                <!-- Compact Header if NO Hero media -->
                <div v-else class="compact-no-hero-header pa-4 pb-0 d-flex justify-space-between align-center">
                  <v-btn icon class="compact-header-btn" @click="showDetailsPanel = false">
                    <v-icon color="grey darken-2">mdi-arrow-left</v-icon>
                  </v-btn>
                  <v-btn icon class="compact-header-btn" @click="toggleFavorite(activeStudio._id)">
                    <v-icon :color="favoriteIds.includes(activeStudio._id) ? 'amber' : 'grey darken-2'">
                      {{ favoriteIds.includes(activeStudio._id) ? "mdi-star" : "mdi-star-outline" }}
                    </v-icon>
                  </v-btn>
                </div>

                <!-- Info Body -->
                <div class="details-body pa-4">
                  <!-- Headline and Open Badge -->
                  <div class="d-flex justify-space-between align-center mb-4 details-title-container">
                    <div class="flex-grow-1 pr-2">
                      <h3 class="studio-detail-title font-weight-black primary-text mb-0">{{ activeStudio.name }}</h3>
                    </div>
                    <span v-if="activeStudio.alwaysCheckin" class="status-badge large green-badge">
                      <span class="status-dot"></span>
                      24H geöffnet
                    </span>
                  </div>

                  <!-- 1. Select Button -->
                  <v-btn x-large block class="mb-6 action-select-btn" @click="pickStudio(activeStudio)">
                    <v-icon left>mdi-check-circle-outline</v-icon>
                    Studio auswählen
                  </v-btn>

                  <!-- 2. Opening Hours -->
                  <div class="opening-hours-section mb-6" v-if="hasOpeningHours(activeStudio)">
                    <div class="mb-3">
                      <h4 class="details-section-title font-weight-black text-subtitle-1 mb-2">ÖFFNUNGSZEITEN</h4>
                      <v-chip-group v-model="activeStudio.selectedHours" mandatory active-class="active-chip" class="hours-selector-chips">
                        <v-chip v-if="activeStudio.alwaysCheckin" value="Checkin" small class="rounded-lg">Check-in</v-chip>
                        <v-chip v-for="hours in activeStudio.openingHours" :key="hours.title" :value="hours.title" small class="rounded-lg">
                          {{ hours.title }}
                        </v-chip>
                      </v-chip-group>
                    </div>

                    <div class="hours-details-table">
                      <template v-if="getOpeningHoursData(activeStudio)">
                        <div
                          v-for="(item, i) in getOpeningHoursData(activeStudio).hours"
                          :key="i"
                          class="hours-detail-row py-2 d-flex justify-space-between align-center">
                          <span class="day-text text-body-2">{{ item.text }}</span>
                          <div class="time-text text-right text-body-2 font-weight-medium">
                            <span v-if="item.from && item.to">{{ item.from }} - {{ item.to }}</span>
                            <span v-if="item.from2 && item.to2"> <br />{{ item.from2 }} - {{ item.to2 }} </span>
                          </div>
                        </div>
                      </template>
                      <div
                        v-else-if="activeStudio.selectedHours === 'Checkin' && activeStudio.alwaysCheckin"
                        class="hours-detail-row py-3 d-flex justify-space-between align-center">
                        <span class="day-text text-body-2">MO - SO / Feiertage</span>
                        <span class="time-text text-green font-weight-bold text-body-2">24H geöffnet</span>
                      </div>
                    </div>
                  </div>

                  <!-- 3. Contact Details Grid -->
                  <div class="contact-details-grid mb-6">
                    <div class="contact-item d-flex align-center py-2" v-if="activeStudio.adresse">
                      <v-icon class="mr-3 text--secondary">mdi-map-marker</v-icon>
                      <div class="text-truncate">
                        <span class="d-block text-caption text--secondary">Adresse</span>
                        <a
                          :href="activeStudio.maps || 'https://maps.google.com/?q=' + encodeURIComponent(activeStudio.adresse)"
                          target="_blank"
                          class="contact-link text-truncate">
                          {{ activeStudio.adresse }}
                        </a>
                      </div>
                    </div>
                    <div class="contact-item d-flex align-center py-2" v-if="activeStudio.email && activeStudio.code !== 'ot'">
                      <v-icon class="mr-3 text--secondary">mdi-email</v-icon>
                      <div class="text-truncate">
                        <span class="d-block text-caption text--secondary">E-Mail</span>
                        <a :href="'mailto:' + activeStudio.email" class="contact-link text-truncate">{{ activeStudio.email }}</a>
                      </div>
                    </div>
                    <div class="contact-item d-flex align-center py-2" v-if="activeStudio.telefon && activeStudio.code !== 'ot'">
                      <v-icon class="mr-3 text--secondary">mdi-phone</v-icon>
                      <div>
                        <span class="d-block text-caption text--secondary">Telefon</span>
                        <a :href="'tel:' + activeStudio.telefon" class="contact-link">{{ activeStudio.telefon }}</a>
                      </div>
                    </div>
                  </div>

                  <!-- 4. Trainer List (RE-DESIGNED WITH HOVER POPUPS) -->
                  <div class="trainer-list-section mb-6" v-if="!trainersLoading && trainers.length > 0">
                    <h4 class="details-section-title font-weight-black text-subtitle-1 mb-3">TRAINER VOR ORT</h4>
                    <div class="trainers-scroll-wrapper">
                      <v-menu
                        v-for="worker in trainers"
                        :key="worker._id"
                        open-on-hover
                        open-on-click
                        close-delay="150"
                        top
                        offset-y
                        min-width="240"
                        rounded="xl"
                        transition="slide-y-transition">
                        <template v-slot:activator="{ on, attrs }">
                          <div v-bind="attrs" v-on="on" class="trainer-card-item">
                            <div class="trainer-avatar-container">
                              <v-avatar size="60" class="trainer-avatar">
                                <v-img :src="worker.picture"></v-img>
                              </v-avatar>
                            </div>
                            <div class="trainer-info-block mt-2">
                              <div class="trainer-name text-caption font-weight-black">{{ worker.firstName }}</div>
                            </div>
                          </div>
                        </template>

                        <!-- Premium Trainer Popup Details Card -->
                        <v-card class="trainer-popover-card pa-4 text-center elevation-10" flat>
                          <v-avatar size="80" class="mx-auto mb-3 trainer-avatar">
                            <v-img :src="worker.picture"></v-img>
                          </v-avatar>
                          <h4 class="font-weight-black text-subtitle-1 mb-1 primary-text">{{ worker.firstName }} {{ worker.lastName || "" }}</h4>
                          <v-divider class="my-2"></v-divider>
                          <p class="text-body-2 text--secondary mb-0 pt-2" style="white-space: normal; line-height: 1.4">
                            {{ worker.title || "Fitnesstrainer" }}
                          </p>
                        </v-card>
                      </v-menu>
                    </div>
                  </div>

                  <!-- Loading state for details -->
                  <v-row v-if="detailsLoading" justify="center" class="my-6">
                    <v-progress-circular indeterminate color="primary"></v-progress-circular>
                  </v-row>

                  <template v-else>
                    <!-- Studio Website Description -->
                    <div
                      v-show="false"
                      v-if="selectedStudioContent && selectedStudioContent.clubSection && selectedStudioContent.clubSection.text"
                      class="website-section mb-6">
                      <h4 class="details-section-title font-weight-black text-subtitle-1 mb-2">ÜBER DIESEN CLUB</h4>
                      <div v-html="selectedStudioContent.clubSection.text" class="club-html-text text-body-2 text--secondary"></div>
                    </div>

                    <!-- Stosszeiten (Popular Times) -->
                    <div v-if="!shouldHideChart && popularTimesData" class="website-section mb-6">
                      <h4 class="details-section-title font-weight-black text-subtitle-1 mb-3">STOSSZEITEN</h4>
                      <div class="day-chips-container mb-4">
                        <div v-for="day in days" :key="day" class="day-chip" :class="{ active: day === selectedDay }" @click="updateChart(day)">
                          {{ day }}
                        </div>
                      </div>

                      <div class="popular-times-chart-wrapper" style="height: 180px">
                        <apexchart ref="chart" type="bar" height="100%" :options="chartOptions" :series="seriesData"></apexchart>
                      </div>

                      <div class="text-center mt-2">
                        <span class="caption-outlastung text-caption px-3 py-1 rounded-pill"> Durchschnittliche Auslastung </span>
                      </div>
                    </div>

                    <!-- 5. Offers & Services (AT THE VERY BOTTOM) -->
                    <div v-if="selectedStudioOffers && selectedStudioOffers.length > 0" class="offers-section mb-6">
                      <h4 class="details-section-title font-weight-black text-subtitle-1 mb-3">ANGEBOTE & SERVICES</h4>
                      <div class="offers-chip-container">
                        <div v-for="offer in selectedStudioOffers" :key="offer._id" class="offer-badge-item">
                          <v-icon small color="primary" class="mr-2">{{ offer.icon || "mdi-checkbox-marked-circle-outline" }}</v-icon>
                          <span class="font-weight-bold text-caption">{{ offer.title }}</span>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </transition>
        </div>

        <!-- Leaflet Map background -->
        <l-map v-if="!loading" ref="map" :zoom="zoom" :center="center" class="map-background" :key="mapKey" :options="{ zoomControl: false }">
          <l-tile-layer :url="attributionUrl" :attribution="attribution" />

          <l-marker
            v-for="m in markers"
            :key="m.id"
            :icon="getMarkerIcon(m.id)"
            :lat-lng="[m.position.lat, m.position.lng]"
            @click="onMarkerClick(m.id)" />
        </l-map>

        <!-- Mobile Quick Studio Preview Card (Map Mode) -->
        <transition name="slide-up">
          <div class="mobile-studio-preview glass-card pa-4 elevation-8" v-if="$vuetify.breakpoint.smAndDown && mobileView === 'map' && activeStudio">
            <div class="d-flex justify-space-between align-start">
              <div class="flex-grow-1 pr-2">
                <h4 class="font-weight-black text-subtitle-1 primary-text mb-1">{{ activeStudio.name }}</h4>
                <p class="text-caption text--secondary mb-2 text-truncate">{{ activeStudio.adresse }}</p>
                <span v-if="activeStudio.alwaysCheckin" class="status-badge green-badge">
                  <span class="status-dot"></span>
                  24H geöffnet
                </span>
              </div>
              <v-btn icon @click="toggleFavorite(activeStudio._id)" class="favorite-btn mr-n2 mt-n2">
                <v-icon :color="favoriteIds.includes(activeStudio._id) ? 'amber' : 'grey'">
                  {{ favoriteIds.includes(activeStudio._id) ? "mdi-star" : "mdi-star-outline" }}
                </v-icon>
              </v-btn>
            </div>
            <div class="d-flex align-center gap-3 mt-4">
              <v-btn class="flex-grow-1 font-weight-bold preview-select-btn" height="42" @click="pickStudio(activeStudio)"> Auswählen </v-btn>
              <v-btn class="font-weight-bold preview-info-btn px-4" height="42" @click="showDetailsSheet = true"> Details </v-btn>
            </div>
          </div>
        </transition>

        <!-- Mobile Details Fullscreen Sheet -->
        <v-dialog
          v-model="showDetailsSheet"
          fullscreen
          hide-overlay
          transition="dialog-bottom-transition"
          v-if="$vuetify.breakpoint.smAndDown && activeStudio">
          <v-card class="mobile-details-sheet d-flex flex-column">
            <!-- Sheet Content -->
            <v-card-text class="pa-0 flex-grow-1 overflow-y-auto">
              <!-- Hero Content -->
              <div v-if="heroPictures && heroPictures.length > 0" class="details-hero">
                <!-- Floating transparent header controls over video -->
                <div class="hero-overlay-controls">
                  <v-btn icon dark @click="showDetailsSheet = false" class="floating-hero-btn back">
                    <v-icon>mdi-arrow-left</v-icon>
                  </v-btn>
                  <v-btn icon dark @click="toggleFavorite(activeStudio._id)" class="floating-hero-btn favorite">
                    <v-icon :color="favoriteIds.includes(activeStudio._id) ? 'amber' : 'white'">
                      {{ favoriteIds.includes(activeStudio._id) ? "mdi-star" : "mdi-star-outline" }}
                    </v-icon>
                  </v-btn>
                </div>

                <div v-if="heroVideoSrc && false" class="video-wrapper">
                  <div v-if="videoLoading" class="video-loader-overlay">
                    <v-progress-circular indeterminate color="primary"></v-progress-circular>
                  </div>
                  <video :src="heroVideoSrc" autoplay muted loop playsinline class="hero-media-element" @loadedmetadata="onVideoLoaded"></video>
                </div>
                <div v-else class="gallery-wrapper">
                  <v-carousel height="100%" hide-delimiters hide-delimiter-background cycle interval="5000" class="hero-carousel">
                    <v-carousel-item v-for="(pic, i) in heroPictures" :key="i" :src="getDynamicPictureSrc(pic)" cover></v-carousel-item>
                  </v-carousel>
                </div>
              </div>

              <!-- Compact Header if NO Hero media -->
              <div v-else class="compact-no-hero-header pa-4 pb-0 d-flex justify-space-between align-center">
                <v-btn icon class="compact-header-btn" @click="showDetailsSheet = false">
                  <v-icon color="grey darken-2">mdi-arrow-left</v-icon>
                </v-btn>
                <v-btn icon class="compact-header-btn" @click="toggleFavorite(activeStudio._id)">
                  <v-icon :color="favoriteIds.includes(activeStudio._id) ? 'amber' : 'grey darken-2'">
                    {{ favoriteIds.includes(activeStudio._id) ? "mdi-star" : "mdi-star-outline" }}
                  </v-icon>
                </v-btn>
              </div>

              <!-- Content Body -->
              <div class="details-body pa-4">
                <!-- Title & 24H Badge -->
                <div class="d-flex justify-space-between align-center mb-4 details-title-container">
                  <div class="flex-grow-1 pr-2">
                    <h3 class="studio-detail-title font-weight-black primary-text mb-0">{{ activeStudio.name }}</h3>
                  </div>
                  <span v-if="activeStudio.alwaysCheckin" class="status-badge large green-badge">
                    <span class="status-dot"></span>
                    24H geöffnet
                  </span>
                </div>

                <!-- 1. Select & Map Actions Row (Side-by-Side) -->
                <div class="details-actions-row mb-6">
                  <v-btn x-large class="flex-grow-1 action-select-btn" @click="pickStudio(activeStudio)">
                    <v-icon left>mdi-check-circle-outline</v-icon>
                    Studio auswählen
                  </v-btn>
                  <v-btn x-large class="action-map-btn" @click="showOnMap(activeStudio)">
                    <v-icon>mdi-map</v-icon>
                  </v-btn>
                </div>

                <!-- 2. Opening Hours -->
                <div class="opening-hours-section mb-6" v-if="hasOpeningHours(activeStudio)">
                  <div class="mb-3">
                    <h4 class="details-section-title font-weight-black text-subtitle-1 mb-2">ÖFFNUNGSZEITEN</h4>
                    <v-chip-group v-model="activeStudio.selectedHours" mandatory active-class="active-chip" class="hours-selector-chips">
                      <v-chip v-if="activeStudio.alwaysCheckin" value="Checkin" small class="rounded-lg">Check-in</v-chip>
                      <v-chip v-for="hours in activeStudio.openingHours" :key="hours.title" :value="hours.title" small class="rounded-lg">
                        {{ hours.title }}
                      </v-chip>
                    </v-chip-group>
                  </div>

                  <div class="hours-details-table">
                    <template v-if="getOpeningHoursData(activeStudio)">
                      <div
                        v-for="(item, i) in getOpeningHoursData(activeStudio).hours"
                        :key="i"
                        class="hours-detail-row py-2 d-flex justify-space-between align-center">
                        <span class="day-text text-body-2">{{ item.text }}</span>
                        <div class="time-text text-right text-body-2 font-weight-medium">
                          <span v-if="item.from && item.to">{{ item.from }} - {{ item.to }}</span>
                          <span v-if="item.from2 && item.to2"> <br />{{ item.from2 }} - {{ item.to2 }} </span>
                        </div>
                      </div>
                    </template>
                    <div
                      v-else-if="activeStudio.selectedHours === 'Checkin' && activeStudio.alwaysCheckin"
                      class="hours-detail-row py-3 d-flex justify-space-between align-center">
                      <span class="day-text text-body-2">MO - SO / Feiertage</span>
                      <span class="time-text text-green font-weight-bold text-body-2">24H geöffnet</span>
                    </div>
                  </div>
                </div>

                <!-- 3. Contact Grid -->
                <div class="contact-details-grid mb-6">
                  <div class="contact-item d-flex align-center py-2" v-if="activeStudio.adresse">
                    <v-icon class="mr-3 text--secondary">mdi-map-marker</v-icon>
                    <div>
                      <span class="d-block text-caption text--secondary">Adresse</span>
                      <a
                        :href="activeStudio.maps || 'https://maps.google.com/?q=' + encodeURIComponent(activeStudio.adresse)"
                        target="_blank"
                        class="contact-link">
                        {{ activeStudio.adresse }}
                      </a>
                    </div>
                  </div>
                  <div class="contact-item d-flex align-center py-2" v-if="activeStudio.email && activeStudio.code !== 'ot'">
                    <v-icon class="mr-3 text--secondary">mdi-email</v-icon>
                    <div>
                      <span class="d-block text-caption text--secondary">E-Mail</span>
                      <a :href="'mailto:' + activeStudio.email" class="contact-link">{{ activeStudio.email }}</a>
                    </div>
                  </div>
                  <div class="contact-item d-flex align-center py-2" v-if="activeStudio.telefon && activeStudio.code !== 'ot'">
                    <v-icon class="mr-3 text--secondary">mdi-phone</v-icon>
                    <div>
                      <span class="d-block text-caption text--secondary">Telefon</span>
                      <a :href="'tel:' + activeStudio.telefon" class="contact-link">{{ activeStudio.telefon }}</a>
                    </div>
                  </div>
                </div>

                <!-- 4. Trainer List (RE-DESIGNED WITH HOVER POPUPS) -->
                <div class="trainer-list-section mb-6" v-if="!trainersLoading && trainers.length > 0">
                  <h4 class="details-section-title font-weight-black text-subtitle-1 mb-3">TRAINER VOR ORT</h4>
                  <div class="trainers-scroll-wrapper">
                    <v-menu
                      v-for="worker in trainers"
                      :key="worker._id"
                      open-on-hover
                      open-on-click
                      close-delay="150"
                      top
                      offset-y
                      min-width="240"
                      rounded="xl"
                      transition="slide-y-transition">
                      <template v-slot:activator="{ on, attrs }">
                        <div v-bind="attrs" v-on="on" class="trainer-card-item">
                          <div class="trainer-avatar-container">
                            <v-avatar size="60" class="trainer-avatar">
                              <v-img :src="worker.picture"></v-img>
                            </v-avatar>
                          </div>
                          <div class="trainer-info-block mt-2">
                            <div class="trainer-name text-caption font-weight-black">{{ worker.firstName }}</div>
                          </div>
                        </div>
                      </template>

                      <!-- Premium Trainer Popup Details Card -->
                      <v-card class="trainer-popover-card pa-4 text-center elevation-10" flat>
                        <v-avatar size="80" class="mx-auto mb-3 trainer-avatar">
                          <v-img :src="worker.picture"></v-img>
                        </v-avatar>
                        <h4 class="font-weight-black text-subtitle-1 mb-1 primary-text">{{ worker.firstName }} {{ worker.lastName || "" }}</h4>
                        <v-divider class="my-2"></v-divider>
                        <p class="text-body-2 text--secondary mb-0 pt-2" style="white-space: normal; line-height: 1.4">
                          {{ worker.title || "Fitnesstrainer" }}
                        </p>
                      </v-card>
                    </v-menu>
                  </div>
                </div>

                <!-- Spinner details -->
                <v-row v-if="detailsLoading" justify="center" class="my-6">
                  <v-progress-circular indeterminate color="primary"></v-progress-circular>
                </v-row>

                <template v-else>
                  <!-- Website Content text -->
                  <div
                    v-if="selectedStudioContent && selectedStudioContent.clubSection && selectedStudioContent.clubSection.text"
                    class="website-section mb-6">
                    <h4 class="details-section-title font-weight-black text-subtitle-1 mb-2">ÜBER DIESEN CLUB</h4>
                    <div v-html="selectedStudioContent.clubSection.text" class="club-html-text text-body-2 text--secondary"></div>
                  </div>

                  <!-- Stosszeiten (Popular Times) -->
                  <div v-if="!shouldHideChart && popularTimesData" class="website-section mb-6">
                    <h4 class="details-section-title font-weight-black text-subtitle-1 mb-3">STOSSZEITEN</h4>
                    <div class="day-chips-container mb-4">
                      <div v-for="day in days" :key="day" class="day-chip" :class="{ active: day === selectedDay }" @click="updateChart(day)">
                        {{ day }}
                      </div>
                    </div>

                    <div class="popular-times-chart-wrapper" style="height: 180px">
                      <apexchart ref="chartMobile" type="bar" height="100%" :options="chartOptions" :series="seriesData"></apexchart>
                    </div>

                    <div class="text-center mt-2">
                      <span class="caption-outlastung text-caption px-3 py-1 rounded-pill"> Durchschnittliche Auslastung </span>
                    </div>
                  </div>

                  <!-- 5. Offers & Services (AT THE VERY BOTTOM) -->
                  <div v-if="selectedStudioOffers && selectedStudioOffers.length > 0" class="offers-section mb-6">
                    <h4 class="details-section-title font-weight-black text-subtitle-1 mb-3">ANGEBOTE & SERVICES</h4>
                    <div class="offers-chip-container">
                      <div v-for="offer in selectedStudioOffers" :key="offer._id" class="offer-badge-item">
                        <v-icon small color="primary" class="mr-2">{{ offer.icon || "mdi-checkbox-marked-circle-outline" }}</v-icon>
                        <span class="font-weight-bold text-caption">{{ offer.title }}</span>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </v-card-text>
          </v-card>
        </v-dialog>

        <!-- Floating Mobile view Toggle: [ Karte | Liste ] -->
        <div class="mobile-view-toggle glass-card pa-1" v-if="$vuetify.breakpoint.smAndDown">
          <button class="toggle-btn" :class="{ active: mobileView === 'map' }" @click="mobileView = 'map'">
            <v-icon left size="16">mdi-map</v-icon>
            Karte
          </button>
          <button class="toggle-btn" :class="{ active: mobileView === 'list' }" @click="mobileView = 'list'">
            <v-icon left size="16">mdi-format-list-bulleted</v-icon>
            Liste
          </button>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "../../api"
import socketService from "../../socketService"
import { mapGetters, mapActions, mapState } from "vuex"
import L from "leaflet"
import { LMap, LTileLayer, LMarker } from "vue2-leaflet"
import userService from "../../services/userService"
import { store } from "@/store/stores"
import "leaflet/dist/leaflet.css"
import VueApexCharts from "vue-apexcharts"

// Setup Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
})

export default {
  name: "StudioPickerModern",

  components: { LMap, LTileLayer, LMarker, apexchart: VueApexCharts },

  props: {
    visible: { type: Boolean, default: false },
  },

  data: () => ({
    userService,
    mapKey: 0,
    loading: false,
    dialogVisible: false,
    wasNavVisible: false,
    studios: [],
    selectedCanton: null,
    activeStudioId: null,
    targetStudioId: null,
    zoom: 9,
    center: [47.3769, 8.5417], // Centered around Zurich
    attributionUrl: "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=cb1_2bw3_1_8b4b26cb54c8063660562055",
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',

    // Redesign elements
    searchQuery: "",
    activeTab: "all",
    mobileView: "map",
    showDetailsPanel: false,
    showDetailsSheet: false,
    favoriteIds: [],

    // Website Details
    selectedStudioContent: null,
    selectedStudioOffers: [],
    detailsLoading: false,
    videoLoading: true,
    url: "https://app.wellcomefit.ch",

    // Redesigned Trainer list variables
    trainers: [],
    trainersLoading: false,

    // Popular Times (Stosszeiten)
    popularTimesData: null,
    popularTimesLoading: false,
    shouldHideChart: false,
    days: ["MO", "DI", "MI", "DO", "FR", "SA", "SO"],
    selectedDay: null,
    globalMax: 1,
    seriesData: [],
    chartOptions: {
      chart: {
        type: "bar",
        toolbar: { show: false },
        sparkline: { enabled: false },
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 800,
          animateGradually: {
            enabled: true,
            delay: 150,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350,
          },
        },
      },
      plotOptions: {
        bar: {
          columnWidth: "65%",
          borderRadius: 4,
          borderRadiusApplication: "end",
          distributed: true,
        },
      },
      dataLabels: { enabled: false },
      xaxis: {
        categories: Array.from({ length: 19 }, (_, i) => `${i + 5}h`),
        labels: { show: false },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        show: false,
        max: 100,
      },
      grid: { show: false },
      tooltip: {
        theme: "dark",
        items: { display: "flex" },
        x: { show: true },
        y: {
          title: {
            formatter: () => "",
          },
          formatter: function (value) {
            if (value <= 35) return "Viel Freiraum"
            if (value <= 75) return "Angenehm belebt"
            return "Beliebte Zeit"
          },
        },
      },
      legend: { show: false },
      colors: ["#10b981"],
    },
  }),

  computed: {
    ...mapState("navigationStoreNotPersisted", ["navigationVisible"]),
    ...mapState("navigationStore", ["reservationTypes"]),
    ...mapState("studioStore", ["selectedStudio"]),
    ...mapGetters("studioStore", ["getStudios", "getSelectedStudioId"]),

    activeStudio() {
      return this.studios.find((s) => s._id === this.activeStudioId) || null
    },

    uniqueCantons() {
      const cantons = this.studios.map((s) => s.canton).filter(Boolean)
      return [...new Set(cantons)].sort()
    },

    markers() {
      return this.studios
        .filter((s) => s.location?.lat && s.location?.long && s.code !== "ot")
        .map((s) => ({
          id: s._id,
          position: { lat: s.location.lat, lng: s.location.long },
        }))
    },

    filteredDirectory() {
      const query = this.searchQuery ? this.searchQuery.toLowerCase() : ""
      let filtered = this.studios.filter((s) => s.code !== "ot")

      // Favorites filter
      if (this.activeTab === "favorites") {
        filtered = filtered.filter((s) => this.favoriteIds.includes(s._id))
      }

      // Canton filter (only apply if not in favorites tab)
      if (this.selectedCanton && this.activeTab !== "favorites") {
        filtered = filtered.filter((s) => s.canton === this.selectedCanton)
      }

      // Search Query filter
      if (query) {
        filtered = filtered.filter(
          (studio) =>
            studio.name.toLowerCase().includes(query) ||
            studio.adresse.toLowerCase().includes(query) ||
            (studio.canton && studio.canton.toLowerCase().includes(query)),
        )
      }

      // Group by Canton
      const grouped = filtered.reduce((acc, studio) => {
        const canton = studio.canton || "Unbekannt"
        if (!acc[canton]) {
          acc[canton] = []
        }
        acc[canton].push(studio)
        return acc
      }, {})

      return Object.entries(grouped)
        .map(([canton, studios]) => ({ canton, studios }))
        .sort((a, b) => {
          const alibeyCanton = "Alibey"
          if (a.canton === alibeyCanton && b.canton !== alibeyCanton) return 1
          if (b.canton === alibeyCanton && a.canton !== alibeyCanton) return -1
          return a.canton.localeCompare(b.canton)
        })
    },

    // Optimized media computed fields
    heroVideoSrc() {
      if (this.selectedStudioContent && this.selectedStudioContent.headerVideo) {
        return this.getDynamicVideoSrc(this.selectedStudioContent.headerVideo)
      }
      // Check if we have pictures. If not, use the fallback video
      const hasPictures = this.selectedStudioContent?.clubSection?.pictures?.length > 0
      if (!hasPictures) {
        return this.getDynamicVideoSrc("video-1737369154374.mp4")
      }
      return null
    },

    heroPictures() {
      if (this.selectedStudioContent?.clubSection?.pictures?.length > 0) {
        return this.selectedStudioContent.clubSection.pictures
      }
      return []
    },
  },

  methods: {
    ...mapActions("navigationStoreNotPersisted", ["toggleNavigationBarVisibility"]),
    ...mapActions("studioStore", ["setSelectedStudio", "setReservationTypes"]),
    ...mapActions("deviceStore", ["resetDeviceList"]),

    async init() {
      await store.restored
      this.loading = true

      // Load favorites from local storage
      this.loadFavorites()

      try {
        let studiosData = null
        if (this.getStudios && this.getStudios.length > 0) {
          studiosData = JSON.parse(JSON.stringify(this.getStudios))
        } else {
          studiosData = await api.fetchStudios()
        }

        this.studios = studiosData.map((studio) => ({
          ...studio,
          selectedHours: studio.alwaysCheckin ? "Checkin" : studio.openingHours?.[0]?.title ?? null,
        }))

        this.$nextTick(() => {
          if (this.selectedStudio) {
            this.targetStudioId = this.selectedStudio._id
            // this.selectedCanton = this.selectedStudio.canton
          } else {
            // Pick first studio if none selected
            const firstStudio = this.studios.filter((s) => s.code !== "ot" && s.code !== "alibey")[0]
            if (firstStudio) {
              this.targetStudioId = firstStudio._id
              //     this.selectedCanton = firstStudio.canton
            }
          }

          this.mapKey += 1 // Force leaflet redraw
        })
      } catch (e) {
        console.error("Failed to fetch studios:", e)
      }

      this.loading = false
    },

    loadFavorites() {
      try {
        const favs = localStorage.getItem("favoriteStudios")
        this.favoriteIds = favs ? JSON.parse(favs) : []
      } catch (e) {
        this.favoriteIds = []
      }
    },

    toggleFavorite(studioId) {
      const idx = this.favoriteIds.indexOf(studioId)
      if (idx > -1) {
        this.favoriteIds.splice(idx, 1)
      } else {
        this.favoriteIds.push(studioId)
      }
      localStorage.setItem("favoriteStudios", JSON.stringify(this.favoriteIds))
    },

    selectStudio(studio) {
      this.activeStudioId = studio._id
      // this.selectedCanton = studio.canton

      if (this.$vuetify.breakpoint.smAndDown) {
        this.showDetailsSheet = true
      } else {
        this.showDetailsPanel = true
      }
      this.scrollToStudio(studio._id)
    },

    onMarkerClick(studioId) {
      const studio = this.studios.find((s) => s._id === studioId)
      if (studio) {
        this.activeStudioId = studioId
        this.selectedCanton = studio.canton
        if (this.$vuetify.breakpoint.smAndDown) {
          // In mobile map view, just activate it and it will show the bottom preview.
          // Scroll map center slightly down to see the pin clearly.
          this.updateMapForActiveStudio(true)
        } else {
          this.showDetailsPanel = true
          this.scrollToStudio(studioId)
        }
      }
    },

    selectCanton(canton) {
      this.selectedCanton = canton
      this.searchQuery = ""

      // Auto zoom to canton center if canton changes
      if (canton) {
        const cantonStudios = this.studios.filter((s) => s.canton === canton && s.location?.lat)
        if (cantonStudios.length > 0) {
          const latSum = cantonStudios.reduce((acc, s) => acc + s.location.lat, 0)
          const lngSum = cantonStudios.reduce((acc, s) => acc + s.location.long, 0)
          const avgLat = latSum / cantonStudios.length
          const avgLng = lngSum / cantonStudios.length

          this.$nextTick(() => {
            if (this.$refs.map) {
              this.$refs.map.mapObject.setView([avgLat - 0.02, avgLng], 11, { animate: true })
            }
          })
        }
      }
    },

    async fetchStudioDetails(studio) {
      if (!studio) return
      this.detailsLoading = true
      this.selectedStudioContent = null
      this.selectedStudioOffers = []
      this.trainers = []
      this.trainersLoading = true
      this.videoLoading = true
      this.popularTimesData = null
      this.shouldHideChart = false

      try {
        const [contentRes, offersRes, trainersData, popularTimesRes] = await Promise.all([
          api.fetchWebsiteContent(studio.code),
          api.fetchOffersAndServicesForStudio(studio._id),
          api.fetchAttendanceTrainers(studio._id).catch(() => []),
          api.fetchPopularTimes(studio.code).catch(() => null),
        ])

        if (contentRes && contentRes.result) {
          this.selectedStudioContent = contentRes.result
        }
        if (offersRes) {
          this.selectedStudioOffers = offersRes.filter((o) => !o.deleted)
        }

        // Parse and sort trainers
        if (trainersData) {
          let filteredTrainers = trainersData.filter((e) => e.firstName !== "Gelöschter")
          filteredTrainers.sort((a, b) => {
            const hasA = a.sort !== undefined && a.sort !== null
            const hasB = b.sort !== undefined && b.sort !== null
            if (hasA && hasB) return a.sort - b.sort
            if (hasA) return -1
            if (hasB) return 1
            return (a.firstName || "").toLowerCase().localeCompare((b.firstName || "").toLowerCase())
          })
          this.trainers = filteredTrainers
        }

        // Parse popular times checkins
        if (popularTimesRes) {
          this.popularTimesData = popularTimesRes

          // Calculate global max for percentage reference
          let maxFound = 0
          Object.values(popularTimesRes).forEach((dayArr) => {
            if (Array.isArray(dayArr)) {
              const dayMax = Math.max(...dayArr)
              if (dayMax > maxFound) maxFound = dayMax
            }
          })

          this.globalMax = maxFound > 0 ? maxFound : 1

          const daysMapping = ["SO", "MO", "DI", "MI", "DO", "FR", "SA"]
          const todayIndex = new Date().getDay()
          this.selectedDay = daysMapping[todayIndex]

          const currentDayData = this.popularTimesData[this.selectedDay]
          if (!currentDayData || currentDayData.every((value) => value <= 5)) {
            this.shouldHideChart = true
          } else {
            this.shouldHideChart = false
            this.updateChart(this.selectedDay)
          }
        } else {
          this.shouldHideChart = true
        }
      } catch (err) {
        console.error("Error fetching website content / offers / trainers / checkins:", err)
        this.shouldHideChart = true
      } finally {
        this.detailsLoading = false
        this.trainersLoading = false
      }
    },

    getOpeningHoursData(studio) {
      if (!studio || !studio.selectedHours || (studio.selectedHours === "Checkin" && studio.alwaysCheckin)) return null
      return studio.openingHours?.find((oh) => oh.title === studio.selectedHours) || null
    },

    hasOpeningHours(studio) {
      if (!studio) return false
      if (studio.alwaysCheckin) return true
      return !!(studio.openingHours && studio.openingHours.length > 0 && studio.openingHours.some((oh) => oh.hours && oh.hours.length > 0))
    },

    updateChart(day) {
      this.selectedDay = day
      if (!this.popularTimesData) return

      const dayData = this.popularTimesData[day]
      if (!dayData) return

      const filteredData = []
      const colors = []
      const currentHour = new Date().getHours()

      // Calculate which day of week index matched (0 = Sunday, 1 = Monday, etc.)
      const daysMapping = ["SO", "MO", "DI", "MI", "DO", "FR", "SA"]
      const targetDayIndex = daysMapping.indexOf(day)
      const isTodaySelected = new Date().getDay() === targetDayIndex

      for (let i = 5; i <= 23; i++) {
        const rawValue = dayData[i] || 0
        let percentage = Math.round((rawValue / this.globalMax) * 100)
        filteredData.push(percentage)

        // Highlight current hour logic
        if (isTodaySelected && i === currentHour) {
          colors.push(this.$vuetify.theme.dark ? "#ffffff" : "#111827")
        } else {
          colors.push("#10b981")
        }
      }

      this.seriesData = [{ name: "Auslastung", data: filteredData }]

      this.$nextTick(() => {
        if (this.$refs.chart) {
          this.$refs.chart.updateOptions({ colors: colors })
        }
        if (this.$refs.chartMobile) {
          this.$refs.chartMobile.updateOptions({ colors: colors })
        }
      })
    },

    isStudioOpen(studio) {
      if (!studio) return false
      if (studio.alwaysCheckin) return true

      const now = new Date()
      const currentDay = now.getDay() // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
      const currentHour = now.getHours()
      const currentMinute = now.getMinutes()
      const currentTimeStr = `${String(currentHour).padStart(2, "0")}:${String(currentMinute).padStart(2, "0")}`

      const dayNames = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"]
      const dayAbbrs = ["SO", "MO", "DI", "MI", "DO", "FR", "SA"]

      const todayName = dayNames[currentDay].toLowerCase()
      const todayAbbr = dayAbbrs[currentDay].toLowerCase()

      const hoursData = this.getOpeningHoursData(studio)
      if (!hoursData || !hoursData.hours) return false

      for (const item of hoursData.hours) {
        if (!item.text) continue
        const text = item.text.toLowerCase()

        let applies = false
        if (text.includes(todayName) || text.includes(todayAbbr)) {
          applies = true
        } else if (text.includes("mo - fr") && currentDay >= 1 && currentDay <= 5) {
          applies = true
        } else if (text.includes("sa - so") && (currentDay === 0 || currentDay === 6)) {
          applies = true
        } else if (text.includes("mo - so") || text.includes("mo-so") || text.includes("täglich")) {
          applies = true
        }

        if (applies) {
          const from = item.from?.trim()
          const to = item.to?.trim()
          const from2 = item.from2?.trim()
          const to2 = item.to2?.trim()

          const inRange = (f, t) => {
            if (!f || !t) return false
            return currentTimeStr >= f && currentTimeStr <= t
          }

          if (inRange(from, to) || inRange(from2, to2)) {
            return true
          }
        }
      }
      return false
    },

    openFavoritesMobile() {
      this.activeTab = "favorites"
      this.mobileView = "list"
    },

    // Media scale optimization
    getVideoSrc(vid, size) {
      return `${vid.replace(".mp4", "")}-${size}.mp4`
    },
    getDynamicVideoSrc(vid) {
      if (!vid) return ""
      const width = window.innerWidth
      if (width >= 1024) {
        return this.url + "/api/website/video/" + this.getVideoSrc(vid, "original")
      } else if (width >= 640) {
        return this.url + "/api/website/video/" + this.getVideoSrc(vid, "large")
      } else {
        return this.url + "/api/website/video/" + this.getVideoSrc(vid, "small")
      }
    },
    onVideoLoaded() {
      this.videoLoading = false
    },

    getPictureSrc(pic, size) {
      if (!pic || !pic.src) return ""
      return `${pic.src.replace(".webp", "")}-${size}.webp`
    },
    getDynamicPictureSrc(pic) {
      if (!pic || !pic.src) return "https://placehold.co/600x400/eee/ccc?text=Bild+lädt..."
      const baseApiUrl = `${this.url}/api/website/image/`
      const width = window.innerWidth
      if (width >= 1024) return baseApiUrl + pic.src
      else if (width >= 640) return baseApiUrl + this.getPictureSrc(pic, "large")
      else return baseApiUrl + this.getPictureSrc(pic, "medium")
    },

    closeDialog() {
      this.dialogVisible = false
    },

    setupObserver() {
      // Stubbed to support historical calls. Redesign uses clean list events.
    },

    getMarkerIcon(studioId) {
      const isActive = this.activeStudioId === studioId
      const iconHtml = `<div class="marker-pin ${isActive ? "active" : ""}"></div>`

      return L.divIcon({
        className: "custom-div-icon",
        html: iconHtml,
        iconSize: [36, 36],
        iconAnchor: [18, 36],
      })
    },

    updateMapForActiveStudio(animate = true) {
      if (!this.activeStudio?.location) return

      const { lat, long: lng } = this.activeStudio.location
      const isMobile = this.$vuetify.breakpoint.smAndDown
      // Shift viewport latitude based on device size so pin is visible above details panels
      const latOffset = isMobile ? -0.0035 : -0.005
      const targetLatLng = [lat + latOffset, lng]

      this.$nextTick(() => {
        if (this.$refs.map) {
          const map = this.$refs.map.mapObject
          const zoomLevel = 14

          const flyAndPan = () => {
            map.flyTo(targetLatLng, zoomLevel, {
              animate: true,
              duration: 0.8,
            })
          }

          if (animate) flyAndPan()
          else map.setView(targetLatLng, zoomLevel)
        }
      })
    },

    scrollToStudio(studioId, smooth = true) {
      this.$nextTick(() => {
        // Desktop scroll to card in side pane
        const container = this.$refs.sidebarContent
        const activeCard = container?.querySelector(".studio-item-card.active")
        if (container && activeCard) {
          container.scrollTo({
            top: activeCard.offsetTop - container.offsetTop - 12,
            behavior: smooth ? "smooth" : "auto",
          })
        }
      })
    },

    async pickStudio(studio) {
      this.loading = true

      try {
        let user = JSON.parse(localStorage.getItem("user"))

        if (user?.user) {
          user.user.studio_id = studio.code
          user.user.studio = studio
          localStorage.setItem("user", JSON.stringify(user))
        }

        await api.pickStudio({ studio_code: studio.code })
        this.setSelectedStudio(studio)

        if (socketService.socket && socketService.socket.connected) {
          socketService.socket.emit("change_studio", { newStudioId: studio.code })
        }

        if (!this.reservationTypes) {
          try {
            const [reservationAvailability] = await Promise.all([api.reservationTool.fetchReservationAvailability()])
            if (reservationAvailability && reservationAvailability.length > 0) {
              this.setReservationTypes(reservationAvailability)
            }
          } catch (e) {}
        }

        this.resetDeviceList()
        this.$emit("studio-picked")
        this.closeDialog()
      } catch (e) {
        console.error("Error picking studio:", e)
      } finally {
        this.loading = false
      }
    },

    showOnMap(studio) {
      this.showDetailsSheet = false
      this.showDetailsPanel = false
      this.mobileView = "map"
      this.activeStudioId = studio._id
      this.updateMapForActiveStudio(true)
    },
  },

  watch: {
    visible: {
      handler(newVal) {
        this.dialogVisible = newVal

        if (newVal) {
          this.wasNavVisible = this.navigationVisible
          if (this.wasNavVisible) this.toggleNavigationBarVisibility()
          this.init()
        } else {
          if (this.wasNavVisible) {
            this.toggleNavigationBarVisibility()
            this.wasNavVisible = false
          }
          let ref = this
          setTimeout(() => {
            ref.loading = false
          }, 300)
        }
      },
      immediate: true,
    },

    dialogVisible(newVal) {
      if (!newVal) this.$emit("update:visible", false)
    },

    studios() {
      this.$nextTick(() => {
        if (this.targetStudioId && this.studios.some((s) => s._id === this.targetStudioId)) {
          this.activeStudioId = this.targetStudioId
          this.scrollToStudio(this.targetStudioId, false)
          this.updateMapForActiveStudio(false)
          this.targetStudioId = null
        } else if (this.studios.length > 0) {
          const firstStudio = this.studios.filter((s) => s.code !== "ot")[0]
          if (firstStudio) {
            this.activeStudioId = firstStudio._id
            this.updateMapForActiveStudio(false)
          }
        }
      })
    },

    activeStudioId(newId, oldId) {
      if (newId && newId !== oldId) {
        const active = this.studios.find((s) => s._id === newId)
        if (active) {
          //  this.selectedCanton = active.canton
          this.fetchStudioDetails(active)
          this.updateMapForActiveStudio(oldId !== null)
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap");

.picker-fullscreen-card {
  font-family: "Inter", sans-serif;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  position: relative;
  background-color: #f3f4f6;
}

// Glassmorphism System
.glass-card {
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.4) !important;
  box-shadow: 0 12px 32px 0 rgba(31, 38, 135, 0.08) !important;
}

.theme--dark {
  .glass-card {
    background: rgba(26, 35, 46, 0.8) !important;
    border-color: rgba(255, 255, 255, 0.08) !important;
    box-shadow: 0 12px 32px 0 rgba(0, 0, 0, 0.3) !important;
  }
  .picker-fullscreen-card {
    background-color: #111827;
  }
}

// Desktop Sidebar Style
.sidebar-container {
  position: absolute;
  left: 24px;
  top: 24px;
  bottom: 24px;
  width: 420px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  border-radius: 24px;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.sidebar-header {
  padding: 24px 24px 16px;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);

  .theme--dark & {
    border-bottom-color: rgba(255, 255, 255, 0.05);
  }
}

.title {
  letter-spacing: 0.05em;
  color: #111827;
  .theme--dark & {
    color: #f3f4f6;
  }
}

.primary-text {
  color: #111827;
  .theme--dark & {
    color: #ffffff;
  }
}

.canton-dropdown-btn {
  font-weight: 700;
  font-size: 0.875rem;
  letter-spacing: 0;
  color: #4b5563 !important;
  background: rgba(0, 0, 0, 0.04);

  .theme--dark & {
    color: #d1d5db !important;
    background: rgba(255, 255, 255, 0.05);
  }
}

.back-to-map-btn {
  background: rgba(0, 150, 63, 0.08) !important;
  color: var(--v-primary-base, #00963f) !important;
  width: 36px !important;
  height: 36px !important;
  border-radius: 50% !important;

  .v-icon {
    color: var(--v-primary-base, #00963f) !important;
    font-size: 20px !important;
  }

  .theme--dark & {
    background: rgba(0, 150, 63, 0.15) !important;
    color: #34d399 !important;
    .v-icon {
      color: #34d399 !important;
    }
  }
}

.canton-list {
  max-height: 300px !important;
  overflow-y: auto !important;
}

.compact-no-hero-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.compact-header-btn {
  background: rgba(0, 0, 0, 0.04) !important;
  border-radius: 50% !important;
  width: 40px !important;
  height: 40px !important;

  .v-icon {
    font-size: 1.25rem !important;
  }

  .theme--dark & {
    background: rgba(255, 255, 255, 0.06) !important;
    .v-icon {
      color: #e5e7eb !important;
    }
  }
}

.search-bar-field {
  ::v-deep .v-input__control {
    min-height: 48px !important;
  }
  ::v-deep .v-input__slot {
    background: rgba(0, 0, 0, 0.03) !important;
    border-radius: 12px !important;
    border: 1px solid rgba(0, 0, 0, 0.04) !important;
    padding: 0 16px !important;
    box-shadow: none !important;

    .theme--dark & {
      background: rgba(255, 255, 255, 0.03) !important;
      border-color: rgba(255, 255, 255, 0.05) !important;
    }
  }
  ::v-deep input {
    font-size: 0.95rem;
    font-weight: 500;
  }
}

.sidebar-tabs {
  margin-top: 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.02);

  ::v-deep .v-tab {
    font-size: 0.875rem;
    letter-spacing: 0.02em;
    text-transform: capitalize;
    color: #6b7280;

    &.v-tab--active {
      color: var(--v-primary-base, #00963f) !important;
    }
  }
  .theme--dark & {
    border-bottom-color: rgba(255, 255, 255, 0.02);
  }
}

.sidebar-scrollable-content {
  flex-grow: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 12px 0 24px;
}

.canton-group {
  margin-bottom: 16px;
}

.canton-subheader {
  font-size: 0.75rem;
  letter-spacing: 0.1em;
  color: #9ca3af;
  font-weight: 800;
}

// Studio Card Style
.studio-item-card {
  background: white !important;
  border-radius: 16px !important;
  border: 1px solid rgba(0, 0, 0, 0.04) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02) !important;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06) !important;
    border-color: rgba(0, 150, 63, 0.15) !important;
  }

  &.active {
    border-color: var(--v-primary-base, #00963f) !important;
    box-shadow: 0 8px 24px rgba(0, 150, 63, 0.1) !important;
    background: rgba(0, 150, 63, 0.02) !important;
  }

  .theme--dark & {
    background: #1f2937 !important;
    border-color: rgba(255, 255, 255, 0.05) !important;

    &.active {
      background: rgba(0, 150, 63, 0.05) !important;
      border-color: var(--v-primary-base, #00963f) !important;
    }
  }
}

.border-top {
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  .theme--dark & {
    border-top-color: rgba(255, 255, 255, 0.05);
  }
}

// Favorite Animation
.favorite-btn {
  transition: transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  &:hover {
    transform: scale(1.15);
  }
  &:active {
    transform: scale(0.9) rotate(-15deg);
  }
}

// Open/Closed Badge
.status-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 8px;

  .status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    margin-right: 6px;
  }

  &.green-badge {
    background: rgba(16, 185, 129, 0.08);
    border: 1px solid rgba(16, 185, 129, 0.2);
    color: #059669;

    .theme--dark & {
      background: rgba(16, 185, 129, 0.15);
      color: #34d399;
      border-color: rgba(16, 185, 129, 0.3);
    }

    .status-dot {
      background: #10b981;
      box-shadow: 0 0 8px #10b981;
      animation: pulse-glow 2s infinite;
    }
  }

  &.large {
    font-size: 0.8rem;
    padding: 6px 12px;
    border-radius: 10px;
  }
}

// Sliding Details panel (Desktop)
.details-panel {
  position: absolute;
  inset: 0;
  background: white;
  z-index: 20;
  display: flex;
  flex-direction: column;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: -8px 0 32px rgba(0, 0, 0, 0.08);

  .theme--dark & {
    background: #111827;
  }
}

.details-scroll-content {
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  scrollbar-width: none;
}

// Details Hero Media Section
.details-hero {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background-color: transparent;
  overflow: hidden;
  flex-shrink: 0;
}

.video-wrapper,
.gallery-wrapper {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.video-loader-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #1f2937;
  z-index: 2;
}

.hero-media-element {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hero-carousel {
  border-bottom: 4px solid var(--v-primary-base, #00963f);
  height: 100% !important;
}

.hero-gradient-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, var(--v-primary-base, #00963f) 0%, #1f2937 100%);
  border-bottom: 4px solid var(--v-primary-base, #00963f);
}

// Floating hero overlay controls
.hero-overlay-controls {
  position: absolute;
  top: 16px;
  left: 16px;
  right: 16px;
  display: flex;
  justify-content: space-between;
  z-index: 10;
  pointer-events: none;
}

.floating-hero-btn {
  pointer-events: auto;
  background: rgba(0, 0, 0, 0.45) !important;
  backdrop-filter: blur(10px) saturate(120%);
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  border-radius: 50%;
  width: 42px !important;
  height: 42px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);

  .v-icon {
    font-size: 1.25rem !important;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.65) !important;
    transform: scale(1.08);
  }
  &:active {
    transform: scale(0.95);
  }
}

// Redesigned Trainer List Styles
.trainers-scroll-wrapper {
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  gap: 12px;
  padding: 8px 4px 12px;

  /* Hide scrollbars */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.trainer-card-item {
  flex-shrink: 0;
  width: 90px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(0, 0, 0, 0.03);
  border-radius: 16px;
  padding: 12px 8px 8px;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    background: rgba(0, 0, 0, 0.04);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
  }

  .theme--dark & {
    background: rgba(255, 255, 255, 0.02);
    border-color: rgba(255, 255, 255, 0.03);

    &:hover {
      background: rgba(255, 255, 255, 0.04);
    }
  }
}

.trainer-popover-card {
  width: 240px;
  max-width: 100%;

  &.theme--light {
    background: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(15px);
  }

  &.theme--dark {
    background: rgba(26, 35, 46, 0.95) !important;
    backdrop-filter: blur(15px);
  }

  .status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }
}

.trainer-avatar-container {
  position: relative;
}

.trainer-avatar {
  border: 2px solid transparent;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);

  &.status-active {
    border-color: #10b981;
    box-shadow: 0 0 10px rgba(16, 185, 129, 0.3);
  }
}

.trainer-status-dot {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid white;

  &.online {
    background-color: #10b981;
  }
  &.offline {
    background-color: #ef4444;
  }

  .theme--dark & {
    border-color: #1f2937;
  }
}

.trainer-info-block {
  width: 100%;
}

.trainer-name {
  color: #111827;
  font-size: 0.75rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  .theme--dark & {
    color: white;
  }
}

.trainer-status-text {
  font-size: 0.65rem;
  font-weight: 700;

  &.online-text {
    color: #10b981;
  }
  &.offline-text {
    color: #9ca3af;
  }
}

.text-micro {
  font-size: 0.65rem;
  line-height: 1.2;
}

.trainer-empty-state {
  background: rgba(0, 0, 0, 0.01);
  border: 1px dashed rgba(0, 0, 0, 0.05);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;

  .theme--dark & {
    background: rgba(255, 255, 255, 0.01);
    border-color: rgba(255, 255, 255, 0.05);
  }
}

// Contacts Grid
.contact-details-grid {
  background: rgba(0, 0, 0, 0.02);
  border-radius: 16px;
  padding: 8px 16px;

  .theme--dark & {
    background: rgba(255, 255, 255, 0.02);
  }
}

.contact-item {
  &:not(:last-child) {
    border-bottom: 1px solid rgba(0, 0, 0, 0.03);
    .theme--dark & {
      border-bottom-color: rgba(255, 255, 255, 0.03);
    }
  }

  .v-icon {
    font-size: 1.25rem;
  }
}

.contact-link {
  color: #111827 !important;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.875rem;

  &:hover {
    color: var(--v-primary-base, #00963f) !important;
  }

  .theme--dark & {
    color: #f3f4f6 !important;
  }
}

// Details Sections
.details-section-title {
  font-size: 0.75rem;
  letter-spacing: 0.1em;
  color: #9ca3af;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  padding-bottom: 4px;

  .theme--dark & {
    border-bottom-color: rgba(255, 255, 255, 0.05);
  }
}

.club-html-text {
  line-height: 1.6;

  ::v-deep p {
    margin-bottom: 8px;
  }
  ::v-deep strong {
    font-weight: 700;
    color: #111827;
  }
  .theme--dark & {
    ::v-deep strong {
      color: white;
    }
  }
}

// Offers Chip Grid
.offers-chip-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.offer-badge-item {
  display: inline-flex;
  align-items: center;
  background: rgba(0, 150, 63, 0.05);
  border: 1px solid rgba(0, 150, 63, 0.1);
  padding: 6px 12px;
  border-radius: 12px;
  color: #111827;

  .theme--dark & {
    background: rgba(0, 150, 63, 0.1);
    color: #f3f4f6;
  }
}

// Opening Hours Style
.hours-selector-chips {
  margin-top: 4px;
  ::v-deep .v-chip {
    font-weight: 600;
  }
}

.active-chip {
  background-color: var(--v-primary-base, #00963f) !important;
  color: white !important;
}

.hours-details-table {
  background: rgba(0, 0, 0, 0.02);
  border-radius: 16px;
  padding: 12px 16px;

  .theme--dark & {
    background: rgba(255, 255, 255, 0.02);
  }
}

.hours-detail-row {
  &:not(:last-child) {
    border-bottom: 1px solid rgba(0, 0, 0, 0.03);
    .theme--dark & {
      border-bottom-color: rgba(255, 255, 255, 0.03);
    }
  }
}

.day-text {
  color: #4b5563;
  font-weight: 500;
  .theme--dark & {
    color: #9ca3af;
  }
}

.time-text {
  color: #111827;
  .theme--dark & {
    color: white;
  }
}

.text-green {
  color: #10b981 !important;
}

// Map Background Styling
.map-background {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  width: 100vw;
  height: 100vh;
}

// Custom Leaflet Marker Pins
:deep(.custom-div-icon) {
  .marker-pin {
    width: 36px;
    height: 36px;
    border-radius: 50% 50% 50% 0;
    background: #eeeeee;
    position: absolute;
    transform: rotate(-45deg);
    left: 50%;
    top: 50%;
    margin: -18px 0 0 -18px;
    border: 2px solid white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &::after {
      content: "";
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: var(--v-primary-base, #00963f);
      transform: rotate(45deg);
      transition: all 0.3s ease;
    }

    &.active {
      background: var(--v-primary-base, #00963f);
      transform: rotate(-45deg) scale(1.18);
      border-color: white;
      box-shadow: 0 6px 20px rgba(0, 150, 63, 0.4);

      &::after {
        background: white;
      }
    }
  }
}

// Transitions
.slide-details-enter-active,
.slide-details-leave-active {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease;
}
.slide-details-enter,
.slide-details-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

// Responsive layout rules
@media (max-width: 768px) {
  .sidebar-container {
    left: 0 !important;
    top: 0 !important;
    width: 100% !important;
    max-width: 100vw !important;
    height: 100vh !important;
    bottom: 0 !important;
    border-radius: 0 !important;
    z-index: 100;
    transform: translateY(100%);
    background: white !important;
    border: none !important;
    box-shadow: none !important;
    box-sizing: border-box !important;
    overflow-x: hidden !important;
    transition: transform 0.45s cubic-bezier(0.32, 0.94, 0.6, 1);

    &.mobile-list-visible {
      transform: translateY(0);
    }
  }

  .theme--dark .sidebar-container {
    background: #111827 !important;
  }

  .sidebar-scrollable-content {
    overflow-x: hidden !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }

  .sidebar-header {
    padding: 16px 16px 12px !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }

  .studios-list {
    padding: 0 16px 16px !important;
  }

  .canton-subheader {
    padding-left: 16px !important;
    padding-right: 16px !important;
  }

  .details-panel {
    position: fixed;
    border-radius: 0;
    z-index: 200;
    transition: transform 0.45s cubic-bezier(0.32, 0.94, 0.6, 1);
  }
}

// Mobile-specific widgets
.mobile-map-header {
  position: absolute;
  left: 12px;
  top: 12px;
  right: 12px;
  height: 52px;
  z-index: 10;
  border-radius: 16px;
  display: flex;
  align-items: center;
  padding: 0 10px;
  border-bottom: 2px solid rgba(0, 150, 63, 0.3) !important;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08) !important;
}

.mobile-search-mock {
  flex-grow: 1;
  display: flex;
  align-items: center;
  color: #4b5563;
  font-size: 0.95rem;
  font-weight: 600;
  height: 100%;
  padding-left: 10px;

  .v-icon {
    font-size: 1.35rem;
    margin-right: 8px;
    color: var(--v-primary-base, #00963f);
  }

  .theme--dark & {
    color: #e5e7eb;
  }
}

.mobile-studio-preview {
  position: absolute;
  left: 16px;
  bottom: 84px;
  right: 16px;
  z-index: 10;
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  background: rgba(255, 255, 255, 0.85) !important;
  backdrop-filter: blur(20px) saturate(180%);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12) !important;

  .gap-3 {
    gap: 12px;
  }

  .theme--dark & {
    background: rgba(26, 35, 46, 0.85) !important;
    border-color: rgba(255, 255, 255, 0.06) !important;
  }
}

.mobile-details-sheet {
  background: white;
  height: 100vh;
  width: 100vw;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 6px;
    left: 50%;
    transform: translateX(-50%);
    width: 40px;
    height: 4px;
    border-radius: 2px;
    background: rgba(255, 255, 255, 0.2);
    z-index: 300;
  }

  .theme--dark & {
    background: #111827;
  }
}

.mobile-view-toggle {
  position: absolute;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 15;
  border-radius: 30px;
  display: flex;

  backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  box-shadow: 0 10px 30px rgba(0, 150, 63, 0.25) !important;
  padding: 4px;

  .toggle-btn {
    border: none;
    outline: none;
    background: transparent;
    padding: 8px 14px;
    border-radius: 26px;
    font-size: 0.825rem;
    font-weight: 800;
    color: #9ca3af;
    display: flex;
    align-items: center;
    white-space: nowrap !important;
    flex-shrink: 0;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    .v-icon {
      color: #9ca3af;
    }

    &.active {
      background: var(--v-primary-base, #00963f);
      color: white;
      .v-icon {
        color: white;
      }
    }
  }
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s ease;
}
.slide-up-enter,
.slide-up-leave-to {
  transform: translateY(120px);
  opacity: 0;
}

// Premium Buttons & Pulse Badges
@keyframes pulse-glow {
  0% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.6);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(16, 185, 129, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
  }
}

.details-title-container {
  margin-bottom: 24px !important;
}

.details-actions-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.studio-detail-title {
  font-family: "Inter", sans-serif;
  font-size: 1.35rem !important;
  font-weight: 800 !important;
  letter-spacing: -0.3px !important;
  line-height: 1.25 !important;
}

.action-select-btn {
  background: var(--v-primary) !important;
  color: white !important;
  font-weight: 800 !important;
  font-size: 0.95rem !important;
  letter-spacing: 0.5px !important;
  text-transform: uppercase !important;
  border-radius: 28px !important;
  height: 52px !important;
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3) !important;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1) !important;
  border: none !important;

  .v-icon {
    font-size: 20px !important;
    margin-right: 8px !important;
    color: white !important;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(16, 185, 129, 0.45) !important;
  }

  &:active {
    transform: translateY(1px) scale(0.98);
    box-shadow: 0 2px 10px rgba(16, 185, 129, 0.2) !important;
  }
}

.action-map-btn {
  background: rgba(16, 185, 129, 0.06) !important;
  border: 1.5px solid rgba(16, 185, 129, 0.3) !important;
  color: #059669 !important;
  border-radius: 50% !important;
  width: 52px !important;
  height: 52px !important;
  min-width: 52px !important;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.02) !important;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1) !important;

  .v-icon {
    font-size: 20px !important;
    color: #059669 !important;
  }

  .theme--dark & {
    background: rgba(16, 185, 129, 0.12) !important;
    border-color: rgba(16, 185, 129, 0.4) !important;
    color: #34d399 !important;

    .v-icon {
      color: #34d399 !important;
    }
  }

  &:hover {
    background: rgba(16, 185, 129, 0.12) !important;
    border-color: rgba(16, 185, 129, 0.5) !important;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(16, 185, 129, 0.15) !important;
  }

  &:active {
    transform: translateY(1px) scale(0.98);
  }
}

.preview-select-btn {
  background: var(--v-primary) !important;
  color: white !important;
  font-weight: 800 !important;
  font-size: 0.85rem !important;
  text-transform: uppercase !important;
  letter-spacing: 0.3px !important;
  border-radius: 12px !important;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.25) !important;
  transition: all 0.2s ease !important;

  &:hover,
  &:active {
    box-shadow: 0 6px 16px rgba(16, 185, 129, 0.35) !important;
    transform: scale(1.02);
  }
}

.preview-info-btn {
  background: rgba(255, 255, 255, 0.8) !important;
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
  color: #374151 !important;
  font-weight: 700 !important;
  font-size: 0.85rem !important;
  text-transform: uppercase !important;
  letter-spacing: 0.3px !important;
  border-radius: 12px !important;
  transition: all 0.2s ease !important;

  .theme--dark & {
    background: rgba(55, 65, 81, 0.5) !important;
    border-color: rgba(255, 255, 255, 0.08) !important;
    color: #f3f4f6 !important;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.03) !important;

    .theme--dark & {
      background: rgba(255, 255, 255, 0.05) !important;
    }
  }
}

.day-chips-container {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 8px;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
}

.day-chip {
  padding: 8px 0;
  width: 42px;
  min-width: 42px;
  text-align: center;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 800;
  color: #6b7280;
  background: #f3f4f6;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);

  .theme--dark & {
    background: rgba(255, 255, 255, 0.05);
    color: #9ca3af;
  }

  &:hover {
    background: #e5e7eb;
    color: #111827;

    .theme--dark & {
      background: rgba(255, 255, 255, 0.1);
      color: #ffffff;
    }
  }

  &.active {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
    color: white !important;
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3) !important;
  }
}

.popular-times-chart-wrapper {
  background: transparent;
}

.caption-outlastung {
  background: #f3f4f6;
  color: #4b5563;
  font-weight: 600;
  letter-spacing: 0.2px;

  .theme--dark & {
    background: rgba(255, 255, 255, 0.05);
    color: #9ca3af;
  }
}
</style>
